import { useState } from 'react';
import { retentionApi, type RetentionAdminView } from '../../api/client';
import { Button, Card, Chip, LoadError, Loading, Notice, SelectField } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Retention (Storage A2, 1.13.0, Teampace 2717; help: staff-archive): how long
 * a company keeps each kind of record when it hasn't chosen (24 months, included, unless changed here),
 * and the switch for real deletions (part 2). Companies choose their own on Settings → Storage and
 * retention; each night counts what's past each company's choice, and its admin confirms the first
 * count. With deleting on, stitch-archive deletes what was confirmed from Stitch's copy (never a
 * company's own). Joey turns it on after the deploy test (the field test retention-delete).
 */
export function RetentionCard() {
  const data = useData(() => retentionApi.admin(), [], { live: false });
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const [asking, setAsking] = useState(false);
  const v = data.data;
  const switchDeletions = async (on: boolean) => {
    setBusy(true);
    setSaid(null);
    try {
      await retentionApi.setDeletions(on);
      setAsking(false);
      data.reload();
      setSaid({ tone: 'ok', text: on ? 'Deleting is on. Within the hour, what companies’ admins confirmed goes from Stitch’s copy.' : 'Deleting is off. Counts go on; nothing is deleted.' });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy(false);
    }
  };
  const save = async (days: number) => {
    setBusy(true);
    setSaid(null);
    try {
      await retentionApi.setDefault(days);
      data.reload();
      setSaid({ tone: 'ok', text: 'Saved. Tonight’s counts use it for every company that hasn’t chosen.' });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Retention" action={v ? <Chip tone={v.deletionsOn ? 'warn' : 'neutral'}>{v.deletionsOn ? 'Deleting' : 'Counting only'}</Chip> : undefined}>
      <div className="card-body stack">
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Loading retention" />
        ) : (
          <>
            <p className="muted small">
              How long Stitch’s archive keeps each kind of record for a company that hasn’t chosen. Companies choose shorter or longer on Settings → Storage and retention; longer than
              24 months is billed for what’s older. Each night Stitch counts what’s past each company’s choice; its admin confirms the first count. Nothing is deleted until deleting is
              turned on below. Only Stitch’s copy is ever deleted: a company’s own storage is the company’s.
            </p>
            <SelectField
              label="Default for every company"
              value={String(v.defaultDays)}
              disabled={busy}
              onChange={(e) => void save(Number(e.target.value))}
              options={v.choices.map((c) => ({ value: String(c.days), label: c.days === v.includedDays ? `${c.label} (included)` : c.label }))}
              hint={v.changed ? `Changed by ${v.changed.byName}, ${shortWhen(v.changed.at)}.` : 'Not changed: 24 months.'}
            />
            <p className="muted small">
              {v.companiesKeepingLonger === 0
                ? 'No company keeps anything longer than 24 months.'
                : `${v.companiesKeepingLonger.toLocaleString('en-US')} ${v.companiesKeepingLonger === 1 ? 'company keeps' : 'companies keep'} something longer than 24 months.`}
            </p>
            <div className="stack">
              <strong>Deleting</strong>
              <p className="muted small">
                {v.deletionsOn
                  ? 'On: each hour, what companies’ admins confirmed (and, each night after, what passes their days) is deleted from Stitch’s copy. A legal hold keeps everything.'
                  : 'Off: counts only. Turn it on after the deploy test (field test “Retention deletes what was confirmed”). On B2, give the archive bucket the lifecycle “Keep only the last version”, so a deleted file is gone for good, not hidden.'}
                {v.deletionsChanged ? ` ${v.deletionsOn ? 'Turned on' : 'Turned off'} by ${v.deletionsChanged.byName}, ${shortWhen(v.deletionsChanged.at)}.` : ''}
              </p>
              {v.deletionsOn ? (
                <span>
                  <Button variant="secondary" disabled={busy} onClick={() => void switchDeletions(false)}>
                    Turn deleting off
                  </Button>
                </span>
              ) : asking ? (
                <span className="row-actions">
                  <Button variant="danger" disabled={busy} onClick={() => void switchDeletions(true)}>
                    {busy ? 'Turning on…' : 'Yes, delete what companies confirmed'}
                  </Button>
                  <Button variant="ghost" onClick={() => setAsking(false)}>
                    Not now
                  </Button>
                </span>
              ) : (
                <span>
                  <Button variant="secondary" disabled={busy} onClick={() => setAsking(true)}>
                    Turn deleting on
                  </Button>
                </span>
              )}
            </div>
            <HotData v={v.hotData} onChanged={data.reload} />
            {said && <Notice tone={said.tone}>{said.text}</Notice>}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Hot data (2717): Stitch's database keeps 90 days of each company's calls (30 days after a company
 * became archive only); older ones are read from the archive, once each one is verified there. Off by
 * default: rows dropped from the database come back only from the archive. Asks first.
 */
function HotData({ v, onChanged }: { v: RetentionAdminView['hotData']; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');
  const flip = async (on: boolean) => {
    setBusy(true);
    setError('');
    try {
      await retentionApi.setHotData(on);
      setAsking(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <strong>
        Stitch’s database keeps {v.days} days <Chip small tone={v.on ? 'warn' : 'neutral'}>{v.on ? 'On' : 'Off'}</Chip>
      </strong>
      <p className="muted small">
        {v.on
          ? `On: each hour, calls older than ${v.days} days (${v.archiveOnlyDays} days after a company became archive only) leave Stitch’s database once each one is verified in Stitch’s archive; Call history reads those days from the archive. A company under a legal hold keeps everything.`
          : `Off: Stitch’s database keeps calls until the records service’s own limit. On, calls older than ${v.days} days leave it once each one is verified in the archive, and Call history reads them from there. Turn it on once the archive has run for a while (System → Archive: Waiting 0) and the field test “Postgres keeps 90 days” has passed.`}
        {v.changed ? ` ${v.on ? 'Turned on' : 'Turned off'} by ${v.changed.byName}, ${shortWhen(v.changed.at)}.` : ''}
        {v.companies ? ` Older calls come from the archive for ${v.companies.toLocaleString('en-US')} ${v.companies === 1 ? 'company' : 'companies'}.` : ''}
      </p>
      {v.on ? (
        <span>
          <Button variant="secondary" disabled={busy} onClick={() => void flip(false)}>
            Stop trimming
          </Button>
        </span>
      ) : asking ? (
        <span className="row-actions">
          <Button variant="danger" disabled={busy} onClick={() => void flip(true)}>
            {busy ? 'Turning on…' : `Yes, keep ${v.days} days in the database`}
          </Button>
          <Button variant="ghost" onClick={() => setAsking(false)}>
            Not now
          </Button>
        </span>
      ) : (
        <span>
          <Button variant="secondary" disabled={busy} onClick={() => setAsking(true)}>
            Keep {v.days} days in the database
          </Button>
        </span>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </div>
  );
}
